/**
 * The words of an assembly drawing.
 *
 * The backend serves the sheet as raw numbers and keys (`AssemblyDrawingSpec`); this
 * module is the single place that turns those into text, so the SVG and the property
 * panel always word and round a value the same way. The number formatting mirrors
 * `visualization.py` exactly — a value that reads "116.999 mm" on the backend-rendered
 * PNG has to read "116.999 mm" here too.
 */

import type {
  DiameterDimension,
  DrawingNote,
  PanelRow,
  PanelSectionKey,
  VerticalDimension,
} from '@/types/assemblyDrawing.types';
import type { TFunction } from 'i18next';

/** Root of the drawing vocabulary inside the translation files. */
const NS = 'toolManagement.assemblyDrawing';

/**
 * EN DASH, not a hyphen: hyperMILL writes this glyph for "no value" and the customer
 * reads our sheet next to theirs.
 */
export const NO_VALUE = '–';

/**
 * Python's `%g` for the magnitudes a drawing carries: six significant digits with the
 * trailing zeros dropped.
 */
function formatSignificant(value: number): string {
  return Number.parseFloat(value.toPrecision(6)).toString();
}

/**
 * Format a millimetre value the way the hyperMILL dialog does, or a dash for nothing.
 *
 * A value that survives a round to three decimals is written as-is (`12`, `207.1`);
 * anything longer is cut to exactly three (`116.9987134917738` → `116.999`).
 */
export function formatDrawingNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NO_VALUE;
  const rounded = Math.round(value * 1000) / 1000;
  return Math.abs(value - rounded) < 1e-9 ? formatSignificant(value) : value.toFixed(3);
}

/** Format a length or a diameter with its unit, or a dash when there is none. */
export function formatMillimetres(value: number | null | undefined): string {
  return value === null || value === undefined ? NO_VALUE : `${formatDrawingNumber(value)} mm`;
}

/** Format an angle in degrees, or a dash when there is none. */
export function formatDegrees(value: number | null | undefined): string {
  return value === null || value === undefined ? NO_VALUE : `${formatDrawingNumber(value)}°`;
}

/**
 * Write out one vertical dimension arrow: `<label> <value>`.
 *
 * A `length` shaft chamfer is the only dimension naming two numbers — how long the
 * chamfer is and where that puts its end, which is the arrow's own top.
 */
export function verticalDimensionText(dim: VerticalDimension, t: TFunction): string {
  if (dim.key === 'shaft_chamfer_length') {
    return t(`${NS}.verticalDim.shaft_chamfer_length`, {
      value: formatDrawingNumber(dim.value),
      end: formatDrawingNumber(dim.z_to),
    });
  }
  return `${t(`${NS}.verticalDim.${dim.key}`)} ${formatDrawingNumber(dim.value)}`;
}

/** Write out one diameter dimension of the block under the tip. */
export function diameterDimensionText(dim: DiameterDimension, t: TFunction): string {
  return `${t(`${NS}.diameterDim.${dim.key}`)} ${formatDrawingNumber(dim.value)}`;
}

/** Write out one note — a radius carries its `R` inside the label, so each one differs. */
export function noteText(note: DrawingNote, t: TFunction): string {
  return t(`${NS}.note.${note.key}`, { value: formatDrawingNumber(note.value) });
}

/** Heading of one property-panel group. */
export function panelSectionLabel(key: PanelSectionKey, t: TFunction): string {
  return t(`${NS}.panelSection.${key}`);
}

/**
 * Label of one property-panel row.
 *
 * A key the backend adds before this side learns its wording falls back to the raw key,
 * which keeps the row readable instead of printing a whole translation path.
 */
export function panelRowLabel(row: PanelRow, t: TFunction): string {
  return t(`${NS}.panelRow.${row.key}`, { defaultValue: row.key });
}

/**
 * Write out one property-panel value, unit included.
 *
 * The keys handled first carry a closed vocabulary rather than a measurement, so they
 * read out of their own translated maps; everything else follows from the row's unit and
 * the type of its value.
 */
export function panelRowValue(row: PanelRow, t: TFunction): string {
  const { value } = row;
  if (row.key === 'tool_class') {
    const raw = String(value ?? '');
    return t(`${NS}.toolClass.${raw}`, { defaultValue: raw });
  }
  if (row.key === 'shaft_chamfer_mode') {
    const raw = String(value ?? '');
    return raw ? t(`${NS}.chamferMode.${raw}`, { defaultValue: raw }) : NO_VALUE;
  }
  if (row.key === 'purpose') {
    // Same three words the tools table uses, translated once outside the drawing namespace.
    const raw = String(value ?? '');
    return raw ? t(`toolManagement.purpose_${raw}`, { defaultValue: raw }) : NO_VALUE;
  }
  if (row.key === 'geometry_from_contour') return t(`${NS}.geometryFromContour`);
  if (row.key === 'geometry_degraded') return t(`${NS}.geometryDegraded`);
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (value === null || value === undefined) return NO_VALUE;
  if (typeof value === 'string') return value || NO_VALUE;
  if (row.unit === 'mm') return formatMillimetres(value);
  if (row.unit === 'deg') return formatDegrees(value);
  return String(value);
}
